// Typed client for the Worker's /api/v1/actions endpoint. The Worker derives
// identity from the Firebase ID token; the body carries only the request.
export class WorkerRequestError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'WorkerRequestError';
    this.code = code;
    this.status = status;
  }
}

export type WorkerClient = {
  call<T>(type: string, body?: Record<string, unknown>): Promise<T>;
};

type WorkerResponse<T> = { data?: T; error?: { code?: string; message?: string } };

// One POST to the Worker, with its error shape turned into WorkerRequestError.
export async function postToWorker<T>(fetchImpl: typeof fetch, url: string, body: Record<string, unknown>, headers: Record<string, string> = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetchImpl(url, { method: 'POST', headers: { 'Content-Type': 'application/json', ...headers }, body: JSON.stringify(body) });
  } catch {
    throw new WorkerRequestError('network_unavailable', 'Guildite could not be reached. Check your connection and try again.', 0);
  }
  const payload = (await response.json().catch(() => ({}))) as WorkerResponse<T>;
  if (!response.ok || payload.error) {
    throw new WorkerRequestError(
      payload.error?.code || 'request_failed',
      payload.error?.message || 'Something went wrong. Please try again.',
      response.status,
    );
  }
  return payload.data as T;
}

export function createWorkerClient({ baseUrl, idToken, fetchImpl = fetch }: {
  baseUrl: string;
  idToken: () => Promise<string | null>;
  fetchImpl?: typeof fetch;
}): WorkerClient {
  return {
    async call<T>(type: string, body: Record<string, unknown> = {}): Promise<T> {
      const token = await idToken();
      if (!token) throw new WorkerRequestError('authentication_required', 'Sign in to continue.', 401);
      return postToWorker<T>(fetchImpl, `${baseUrl}/api/v1/actions`, { ...body, type }, { Authorization: `Bearer ${token}` });
    },
  };
}
